import { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { CircularProgress, Typography, Box, Chip, Card, CardContent, Divider, Icon, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import moment from "moment";

// Import helper functions from the list configuration
import {
	Schedule as ScheduleIcon,
	CheckCircle as CompletedIcon,
	Warning as WarningIcon,
	Info as InfoIcon,
	LocalShipping as TruckIcon,
	LocationOn as LocationIcon,
	AccessTime as TimeIcon,
	ReportProblem as ForcedIcon,
	SwapHoriz as SwapIcon,
	FileCopy as ConflictIcon,
	ExitToApp as OutsideIcon,
	EventBusy as EventBusyIcon,
	Cancel as CancelIcon,
} from "@material-ui/icons";

const OUTSIDE_SCHEDULE_REASON_LABELS = {
	NO_TRUCK_TRIPS: "No Active Trips",
	DATE_MISMATCH: "Date Mismatch",
	TYPE_MISMATCH: "Type Mismatch",
	YARD_FILTER: "Yard Filter",
	DIFFERENT_LOAD: "Different Load",
	YARD_MISMATCH: "Yard Mismatch",
};

// Sub-reason labels for POSSIBLE_CANCELLATION (carried in warning.metadata.reason).
// Mirrors the backend reason codes set in relayScheduleManager.js.
const POSSIBLE_CANCELLATION_REASON_LABELS = {
	LONGHAUL_TRUCK_NOT_AT_YARD: "LH truck not at yard",
	ASSIGNED_TRUCK_UNAVAILABLE: "Assigned truck unavailable",
};

// Format POSSIBLE_CANCELLATION metadata into a concise sub-label and a longer
// tooltip detail. ASSIGNED_TRUCK_UNAVAILABLE includes per-position
// unavailable_assignments so the chip can call out exactly which slot
// (PICKUP / LONGHAUL / DELIVERY) is on an unavailable truck.
const formatPossibleCancellationMetadata = (warning) => {
	const reason = warning?.metadata?.reason;
	const reasonLabel = reason ? POSSIBLE_CANCELLATION_REASON_LABELS[reason] || reason.replace(/_/g, " ") : null;
	const assignments = Array.isArray(warning?.metadata?.unavailable_assignments)
		? warning.metadata.unavailable_assignments
		: [];
	const assignmentsLabel = assignments.length
		? assignments.map((a) => `${a.position} #${a.truckLabel || a.truckId}${a.date ? ` on ${a.date}` : ""}`).join(", ")
		: null;
	let subLabel = null;
	if (reason === "ASSIGNED_TRUCK_UNAVAILABLE") {
		subLabel = assignmentsLabel ? `Unavailable: ${assignmentsLabel}` : reasonLabel;
	} else if (reasonLabel) {
		subLabel = reasonLabel;
	}
	const tooltipDetailParts = [];
	if (reasonLabel) tooltipDetailParts.push(reasonLabel);
	if (assignmentsLabel) tooltipDetailParts.push(assignmentsLabel);
	return {
		subLabel,
		tooltipDetail: tooltipDetailParts.length ? tooltipDetailParts.join(" — ") : null,
	};
};

const getChipTruckLabel = (schedule) => {
	const covered = schedule.covered_trips?.[0]?.truck?.truckid;
	if (covered) return `#${covered} `;
	const assigned = schedule.truck?.truckid || schedule.assigned_truck?.truckid;
	return assigned ? `#${assigned} ` : "";
};

const appendPlannedTruckTooltip = (schedule, tooltipText) => {
	const coveredTruckId = schedule.covered_trips?.[0]?.truck?.truckid;
	if (
		schedule.original_assigned_truck_truckid &&
		coveredTruckId &&
		coveredTruckId !== schedule.original_assigned_truck_truckid
	) {
		return `${tooltipText}\n📋 Planned truck: #${schedule.original_assigned_truck_truckid}`;
	}
	return tooltipText;
};

const WARNING_REASON_META = {
	LONGHAUL_NOT_PARTNER: { icon: SwapIcon, color: "#0277bd", bg: "#e1f5fe", label: "Not Partner" },
	TRUCK_CONFLICT_SAME_POSITION: { icon: ConflictIcon, color: "#c62828", bg: "#ffebee", label: "Duplicate Truck" },
	CHILD_TRIP_OUTSIDE_SCHEDULE: { icon: OutsideIcon, color: "#ef6c00", bg: "#fff3e0", label: "Outside Schedule" },
	POSSIBLE_CANCELLATION: { icon: CancelIcon, color: "#d32f2f", bg: "#ffebee", label: "Possible Cancellation" },
	NON_ACTIVE_RELAY_TRUCK: { icon: EventBusyIcon, color: "#6a1b9a", bg: "#f3e5f5", label: "Non-Active Truck" },
};

const DEFERRED_REASON_META = {
	MANUAL_DEFERRED: { label: "Manual" },
	NO_PICKUP_TRUCK_ELIGIBLE: { label: "Pickup trucks unavailable" },
	NO_PICKUP_TRUCK: { label: "No pickup truck" },
	NO_LONGHAUL_TRUCK_ELIGIBLE: { label: "Longhaul trucks unavailable" },
	NO_LONGHAUL_TRUCK: { label: "No longhaul truck" },
	NO_DELIVERY_TRUCK_ELIGIBLE: { label: "Delivery trucks unavailable" },
	NO_DELIVERY_TRUCK: { label: "No delivery truck" },
	SCHEDULE_NOT_ACTIVE: { label: "Not active today" },
	INCOMPLETE_CHAIN: { label: "Incomplete chain" },
	INCOMPLETE_INSTANCES: { label: "Incomplete instances" },
	SCHEDULE_CONFLICT: { label: "Schedule conflict" },
	COVERED_BY_ACTIVE_CHAIN: { label: "Covered by active trip" },
	ERROR: { label: "Error" },
};

const FORCED_TOOLTIP = "Forced child trip (outside schedule)";

const useStyles = makeStyles((theme) => ({
	root: {
		padding: theme.spacing(2),
		minWidth: 800,
		maxWidth: 1200,
	},
	chainCard: {
		marginBottom: theme.spacing(2),
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: theme.spacing(1),
	},
	scheduleChip: {
		margin: theme.spacing(0.25),
		fontSize: "0.75rem",
	},
	timelineContainer: {
		display: "flex",
		alignItems: "center",
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
	},
	timelineItem: {
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		flex: 1,
		padding: theme.spacing(1),
	},
	timelineConnector: {
		height: 2,
		backgroundColor: theme.palette.primary.main,
		flex: 1,
		margin: theme.spacing(0, 1),
	},
	statusIcon: {
		fontSize: "1rem",
		marginRight: theme.spacing(0.5),
	},
	headerSection: {
		marginBottom: theme.spacing(3),
	},
	emptyState: {
		textAlign: "center",
		padding: theme.spacing(4),
		color: theme.palette.text.secondary,
	},
}));

// Helper functions
const formatDateTime = (dateTime) => {
	if (!dateTime) return "N/A";
	return moment(dateTime).format("ddd MM/DD HH:mm");
};

const formatTime = (dateTime) => {
	if (!dateTime) return "N/A";
	return moment(dateTime).format("HH:mm");
};

const getScheduleTypeIcon = (type) => {
	switch (type) {
		case "PICKUP":
			return <Icon style={{ color: "#c62828", fontSize: "1rem" }}>local_shipping</Icon>;
		case "LONGHAUL":
			return <Icon style={{ color: "#6a1b9a", fontSize: "1rem" }}>alt_route</Icon>;
		case "DELIVERY":
			return <Icon style={{ color: "#2e7d32", fontSize: "1rem" }}>delivery_dining</Icon>;
		default:
			return <ScheduleIcon style={{ color: "#9e9e9e", fontSize: "1rem" }} />;
	}
};

const getStatusIcon = (hasOverlap, wasAdjusted) => {
	if (hasOverlap) {
		return <WarningIcon style={{ color: "#f44336", fontSize: "1rem" }} />;
	} else if (wasAdjusted) {
		return <WarningIcon style={{ color: "#ff9800", fontSize: "1rem" }} />;
	} else {
		return <CompletedIcon style={{ color: "#4caf50", fontSize: "1rem" }} />;
	}
};

// Helper function to convert relay chain data from search to view format
const convertRelayChainToViewData = (relayChain) => {
	if (!relayChain) return [];

	const schedules = [];
	const baseDate = relayChain.date || moment().format("YYYY-MM-DD");
	const chainId = relayChain.chainId || `relay_chain_${baseDate}`;
	const chainTruck = relayChain.truck || { _id: "truck1", name: "Relay Truck", truckid: "REL001" };

	// Helper to convert a single schedule
	const convertSchedule = (schedule, type, sequence) => {
		if (!schedule) return null;

		return {
			id: schedule.id || `${chainId}_${type}_${sequence}`,
			schedule_id: schedule.schedule_id || schedule.id,
			truck_id: schedule.truck_id || schedule.truck?._id,
			carrier_id: schedule.carrier_id || schedule.carrier,
			schedule_name: schedule.schedule_name || `${type} Schedule`,
			schedule_type: type,
			date: schedule.date || baseDate,
			start_datetime: schedule.start_datetime,
			end_datetime: schedule.end_datetime,
			start_time: schedule.start_time,
			end_time: schedule.end_time,

			// Location info
			origin_type: schedule.origin_type,
			origin_yard: schedule.origin_yard,
			origin_coordinates: schedule.origin_coordinates,
			destination_type: schedule.destination_type,
			destination_yard: schedule.destination_yard,
			destination_coordinates: schedule.destination_coordinates,

			// Operational info
			estimated_duration_hours: schedule.estimated_duration_hours,
			max_concurrent_loads: schedule.max_concurrent_loads,
			equipment_types: schedule.equipment_types,
			max_radius_miles: schedule.max_radius_miles,
			priority: schedule.priority,

			// Truck info
			truck: schedule.truck || chainTruck,
			chain_truck: chainTruck,

			// Adjustment info
			was_adjusted: schedule.was_adjusted || false,
			adjustment_reason: schedule.adjustment_reason,
			conflicts_avoided: schedule.conflicts_avoided || [],
			has_overlap: schedule.has_overlap || false,
			covering_trips: schedule.covering_trips || [],
			covered_trips: schedule.covered_trips || [],
			coverage_status: schedule.coverage_status || relayChain.coverage_status || "AVAILABLE",
			coverage_warnings: schedule.coverage_warnings || [],
			unavailable_trucks: schedule.unavailable_trucks || [],
			coverage_reason_code: schedule.coverage_reason_code || relayChain.coverage_reason_code,
			coverage_reason: schedule.coverage_reason || relayChain.coverage_reason,

			// Chain info
			relay_chain_id: chainId,
			chain_position: type,
			chain_sequence: sequence,
			relay_coordination: schedule.relay_coordination || {
				type: "COMPLETE_RELAY_CHAIN",
				chain_longhaul_schedule:
					relayChain.longhaul?.schedule_name ||
					relayChain.longhaul_schedules?.[0]?.schedule_name ||
					"Longhaul Schedule",
				pickup_schedule:
					relayChain.pickup?.schedule_name || relayChain.pickup_schedules?.[0]?.schedule_name || "Pickup Schedule",
				delivery_schedule:
					relayChain.delivery?.schedule_name ||
					relayChain.delivery_schedules?.[0]?.schedule_name ||
					"Delivery Schedule",
			},

			// Original schedule reference
			original_schedule: schedule.original_schedule,
		};
	};

	let sequenceCounter = 1;

	// Convert pickup schedules (can be multiple)
	if (relayChain.pickup_schedules && Array.isArray(relayChain.pickup_schedules)) {
		relayChain.pickup_schedules.forEach((schedule) => {
			const converted = convertSchedule(schedule, "PICKUP", sequenceCounter++);
			if (converted) schedules.push(converted);
		});
	} else if (relayChain.pickup) {
		const converted = convertSchedule(relayChain.pickup, "PICKUP", sequenceCounter++);
		if (converted) schedules.push(converted);
	}

	// Convert longhaul schedules (can be multiple)
	if (relayChain.longhaul_schedules && Array.isArray(relayChain.longhaul_schedules)) {
		relayChain.longhaul_schedules.forEach((schedule) => {
			const converted = convertSchedule(schedule, "LONGHAUL", sequenceCounter++);
			if (converted) {
				converted.is_chain_anchor = true;
				schedules.push(converted);
			}
		});
	} else if (relayChain.longhaul) {
		const converted = convertSchedule(relayChain.longhaul, "LONGHAUL", sequenceCounter++);
		if (converted) {
			converted.is_chain_anchor = true;
			schedules.push(converted);
		}
	}

	// Convert delivery schedules (can be multiple)
	if (relayChain.delivery_schedules && Array.isArray(relayChain.delivery_schedules)) {
		relayChain.delivery_schedules.forEach((schedule) => {
			const converted = convertSchedule(schedule, "DELIVERY", sequenceCounter++);
			if (converted) schedules.push(converted);
		});
	} else if (relayChain.delivery) {
		const converted = convertSchedule(relayChain.delivery, "DELIVERY", sequenceCounter++);
		if (converted) schedules.push(converted);
	}

	return schedules;
};

const RelayScheduleChainView = ({ dataIds, setTitle, setSize, onDone }) => {
	const classes = useStyles();
	const [data, setData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [groupedChains, setGroupedChains] = useState({});

	// Set up the dialog
	useEffect(() => {
		setTitle?.("Relay Schedules");
	}, []);

	// Memoize the converted relay chain data to prevent infinite re-renders
	// This only recalculates when the actual relay chain reference changes
	const convertedData = useMemo(() => {
		if (!dataIds?.relayChain) return [];
		try {
			return convertRelayChainToViewData(dataIds.relayChain);
		} catch (err) {
			console.error("Error converting relay chain data:", err);
			setError(err.message);
			return [];
		}
	}, [dataIds]);

	// Set data once when component mounts or when convertedData changes
	useEffect(() => {
		setData(convertedData);
		setLoading(false);
	}, [convertedData]);

	// Group schedules by relay chain
	useEffect(() => {
		const grouped = {};

		data.forEach((schedule) => {
			const chainId = schedule.relay_chain_id || schedule.id;

			if (!grouped[chainId]) {
				grouped[chainId] = {
					chainId,
					date: schedule.date,
					chain_truck: schedule.chain_truck,
					schedules: [],
					coordination: schedule.relay_coordination,
					hasOverlap: false,
					coveringTrips: schedule.covering_trips || [],
					coverageStatus: schedule.coverage_status || "AVAILABLE",
					coverageReasonCode: schedule.coverage_reason_code,
					coverageReason: schedule.coverage_reason,
					coverageWarnings: [],
					unavailableTrucks: [],
					wasAdjusted: false,
					hasOutsideSchedule: false,
				};
			}

			grouped[chainId].schedules.push(schedule);

			// Update chain status
			if (schedule.has_overlap) grouped[chainId].hasOverlap = true;
			if (schedule.coverage_reason_code && !grouped[chainId].coverageReasonCode) {
				grouped[chainId].coverageReasonCode = schedule.coverage_reason_code;
				grouped[chainId].coverageReason = schedule.coverage_reason;
			}
			if (schedule.was_adjusted) grouped[chainId].wasAdjusted = true;
			if (schedule.covered_trips?.some((t) => t.outside_schedule === true)) {
				grouped[chainId].hasOutsideSchedule = true;
			}
			if (Array.isArray(schedule.coverage_warnings)) {
				schedule.coverage_warnings.forEach((w) => {
					if (!grouped[chainId].coverageWarnings.some((existing) => existing.code === w.code)) {
						grouped[chainId].coverageWarnings.push(w);
					}
				});
			}
			if (Array.isArray(schedule.unavailable_trucks)) {
				schedule.unavailable_trucks.forEach((ut) => {
					const utId = ut?._id?.toString ? ut._id.toString() : String(ut?._id);
					if (
						!grouped[chainId].unavailableTrucks.some((existing) => {
							const existingId = existing?._id?.toString ? existing._id.toString() : String(existing?._id);
							return existingId === utId;
						})
					) {
						grouped[chainId].unavailableTrucks.push(ut);
					}
				});
			}
		});

		// Sort schedules within each chain by sequence
		Object.values(grouped).forEach((chain) => {
			chain.schedules.sort((a, b) => (a.chain_sequence || 0) - (b.chain_sequence || 0));
		});

		setGroupedChains(grouped);
	}, [data]);

	const renderScheduleChip = (schedule) => {
		const getChipColor = (type) => {
			switch (type) {
				case "PICKUP":
					return { backgroundColor: "#ffebee", color: "#c62828" };
				case "LONGHAUL":
					return { backgroundColor: "#f3e5f5", color: "#6a1b9a" };
				case "DELIVERY":
					return { backgroundColor: "#e8f5e8", color: "#2e7d32" };
				default:
					return { backgroundColor: "#f5f5f5", color: "#666" };
			}
		};

		// Build truck ID prefix if available
		const truckPrefix = getChipTruckLabel(schedule);
		// Build tooltip text with adjustment reason if available
		let tooltipText = `${schedule.truck__view?.label ? `${schedule.truck__view?.label} ` : ""}${
			schedule.schedule_type
		}: ${schedule.schedule_name} (${formatDateTime(schedule.start_datetime)} - ${formatTime(schedule.end_datetime)})`;

		if (schedule.was_adjusted && schedule.adjustment_reason) {
			tooltipText += `\n⚠️ ${schedule.adjustment_reason}`;
		}
		const isCoveredSchedule = schedule.covered_trips?.length > 0;
		const isForcedSchedule = schedule.covered_trips?.some((t) => t.outside_schedule === true);
		if (isCoveredSchedule) {
			tooltipText += `\n📌 Covered by ${schedule.covered_trips
				?.map((trip) => `#${trip.truck?.truckid} - ${trip.load_id}`)
				.join(", ")}`;
		}
		if (isForcedSchedule) {
			tooltipText += "\n⚠️ Forced child trip (outside schedule)";
		}
		tooltipText = appendPlannedTruckTooltip(schedule, tooltipText);
		const scheduleDisplayName = isCoveredSchedule
			? schedule.covered_trips
					?.map((t) => t.load_id)
					.filter(Boolean)
					.join(", ") || schedule.schedule_name
			: schedule.schedule_name;

		return (
			<Tooltip key={schedule.id} title={tooltipText}>
				<Chip
					size="small"
					label={
						<>
							{`${truckPrefix}${schedule.schedule_type.charAt(0)}: ${scheduleDisplayName}`}
							{isForcedSchedule && (
								<Tooltip title={FORCED_TOOLTIP}>
									<span style={{ display: "inline-flex", marginLeft: 4, verticalAlign: "middle" }}>
										<ForcedIcon style={{ fontSize: "0.8rem" }} />
									</span>
								</Tooltip>
							)}
						</>
					}
					style={{
						...getChipColor(schedule.schedule_type),
						margin: "2px",
						fontSize: "0.75rem",
					}}
					icon={
						isCoveredSchedule ? (
							isForcedSchedule ? (
								<ForcedIcon style={{ fontSize: "0.8rem" }} />
							) : (
								<CompletedIcon style={{ fontSize: "0.8rem" }} />
							)
						) : schedule.was_adjusted ? (
							<WarningIcon style={{ fontSize: "0.8rem" }} />
						) : (
							getScheduleTypeIcon(schedule.schedule_type)
						)
					}
					className={classes.scheduleChip}
				/>
			</Tooltip>
		);
	};

	const renderTimelineView = (chain) => {
		const sortedSchedules = [...chain.schedules].sort((a, b) => (a.chain_sequence || 0) - (b.chain_sequence || 0));

		return (
			<Box className={classes.timelineContainer}>
				{sortedSchedules.map((schedule, index) => (
					<Box key={schedule.id} display="flex" alignItems="center" flex={1}>
						<Box className={classes.timelineItem}>
							<Box display="flex" alignItems="center" marginBottom={1}>
								{getScheduleTypeIcon(schedule.schedule_type)}
								<Typography variant="caption" style={{ marginLeft: 4 }}>
									{schedule.schedule_type}
								</Typography>
							</Box>
							{(schedule.covered_trips?.[0]?.truck?.truckid || schedule.truck?.truckid) && (
								<Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
									Truck #{schedule.covered_trips?.[0]?.truck?.truckid || schedule.truck.truckid}
								</Typography>
							)}
							<Typography variant="body2" style={{ fontWeight: 500 }}>
								{schedule.covered_trips?.length > 0
									? schedule.covered_trips
											?.map((t) => t.load_id)
											.filter(Boolean)
											.join(", ") || schedule.schedule_name
									: schedule.schedule_name}
							</Typography>
							<Typography variant="caption" color="textSecondary">
								{formatDateTime(schedule.start_datetime)} - {formatTime(schedule.end_datetime)}
							</Typography>
							<Typography variant="caption" color="textSecondary">
								Yard: {schedule.origin_yard?.city}, {schedule.origin_yard?.state}
							</Typography>
						</Box>
						{index < sortedSchedules.length - 1 && <Box className={classes.timelineConnector} />}
					</Box>
				))}
			</Box>
		);
	};

	const renderChainCard = (chain) => {
		const {
			chainId,
			schedules,
			coordination,
			hasOverlap,
			wasAdjusted,
			truck,
			chain_truck,
			coverageStatus,
			coverageReasonCode,
			coverageReason,
			coveringTrips,
			coverageWarnings,
			unavailableTrucks,
			hasOutsideSchedule,
		} = chain;

		// Calculate pickup date range
		const pickupSchedules = schedules.filter((s) => s.schedule_type === "PICKUP");
		let earliestPickup = null;
		let latestPickup = null;
		if (pickupSchedules.length > 0) {
			pickupSchedules.forEach((schedule) => {
				const startTime = moment(schedule.start_datetime);
				const endTime = moment(schedule.end_datetime);
				if (!earliestPickup || startTime.isBefore(earliestPickup)) {
					earliestPickup = startTime;
				}
				if (!latestPickup || endTime.isAfter(latestPickup)) {
					latestPickup = endTime;
				}
			});
		}

		// Calculate delivery date range
		const deliverySchedules = schedules.filter((s) => s.schedule_type === "DELIVERY");
		let earliestDelivery = null;
		let latestDelivery = null;
		if (deliverySchedules.length > 0) {
			deliverySchedules.forEach((schedule) => {
				const startTime = moment(schedule.start_datetime);
				const endTime = moment(schedule.end_datetime);
				if (!earliestDelivery || startTime.isBefore(earliestDelivery)) {
					earliestDelivery = startTime;
				}
				if (!latestDelivery || endTime.isAfter(latestDelivery)) {
					latestDelivery = endTime;
				}
			});
		}

		// Collect unique origins (from pickup and longhaul)
		const origins = [];
		schedules.forEach((schedule) => {
			if (schedule.schedule_type === "PICKUP" && schedule.origin_yard) {
				if (schedule.origin_yard.search_city && !schedule.was_adjusted && !schedule.covered_trips?.length > 0) {
					origins.push(`${schedule.origin_yard.search_city}, ${schedule.origin_yard.search_state}`);
				} else {
					origins.push(`${schedule.origin_yard.city}, ${schedule.origin_yard.state}`);
				}
			}
		});
		const uniqueOrigins = [...new Set(origins)];

		// Collect unique destinations (from longhaul and delivery)
		const destinations = [];
		schedules.forEach((schedule) => {
			if (schedule.schedule_type === "DELIVERY" && schedule.destination_yard) {
				if (schedule.destination_yard.search_city && !schedule.was_adjusted && !schedule.covered_trips?.length > 0) {
					destinations.push(`${schedule.destination_yard.search_city}, ${schedule.destination_yard.search_state}`);
				} else {
					destinations.push(`${schedule.destination_yard.city}, ${schedule.destination_yard.state}`);
				}
			}
		});
		const uniqueDestinations = [...new Set(destinations)];

		return (
			<Card key={chainId} className={classes.chainCard}>
				<CardContent>
					{/* Header */}
					<Box display="flex" justifyContent="space-between" alignItems="flex-start" marginBottom={2}>
						<Box flex={1}>
							<Box display="flex" alignItems="center" marginBottom={1}>
								{getStatusIcon(hasOverlap, wasAdjusted)}
								{chain_truck && (
									<>
										<TruckIcon style={{ marginRight: 8, color: "#666" }} />

										<Typography variant="body2" style={{ fontWeight: 500 }}>
											#{chain_truck.truckid}
										</Typography>
										<Typography variant="caption" color="textSecondary">
											{chain_truck.name}
										</Typography>
									</>
								)}
							</Box>
							<Typography variant="body2" color="textSecondary">
								{moment(chain.date).format("dddd, MMMM DD, YYYY")}
							</Typography>
						</Box>
					</Box>

					{/* Date Ranges and Locations Summary */}
					<Box marginBottom={2} padding={2} style={{ backgroundColor: "#f5f5f5", borderRadius: 8 }}>
						<Box display="flex" flexWrap="wrap" gap={2}>
							{earliestPickup && (
								<Box flex="1" minWidth={200}>
									<Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
										PICKUP DATE
									</Typography>
									<Typography variant="body2">
										{formatDateTime(earliestPickup)} - {formatTime(latestPickup)}
									</Typography>
								</Box>
							)}
							{uniqueOrigins.length > 0 && (
								<Box flex="1" minWidth={200}>
									<Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
										ORIGIN
									</Typography>
									<Typography variant="body2">{uniqueOrigins.join(", ")}</Typography>
								</Box>
							)}
						</Box>
						<Box display="flex" flexWrap="wrap" gap={2} marginTop={1}>
							{earliestDelivery && (
								<Box flex="1" minWidth={200}>
									<Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
										DELIVERY DATE
									</Typography>
									<Typography variant="body2">
										{formatDateTime(earliestDelivery)} - {formatTime(latestDelivery)}
									</Typography>
								</Box>
							)}
							{uniqueDestinations.length > 0 && (
								<Box flex="1" minWidth={200}>
									<Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
										DESTINATION
									</Typography>
									<Typography variant="body2">{uniqueDestinations.join(", ")}</Typography>
								</Box>
							)}
						</Box>
					</Box>

					{/* Status indicators */}
					{(hasOverlap ||
						wasAdjusted ||
						coverageStatus === "COMPLETED" ||
						coverageStatus === "DEFERRED" ||
						coveringTrips?.length > 0 ||
						hasOutsideSchedule ||
						coverageWarnings?.length > 0 ||
						unavailableTrucks?.length > 0) && (
						<Box marginBottom={2}>
							{coverageStatus === "DEFERRED" && (
								<Tooltip title={coverageReason || "Chain deferred — resources unavailable"}>
									<Chip
										size="small"
										label={
											DEFERRED_REASON_META[coverageReasonCode]?.label ||
											coverageReasonCode?.replace(/_/g, " ") ||
											"Deferred"
										}
										style={{ backgroundColor: "#fff3e0", color: "#e65100", marginRight: 8 }}
										icon={<WarningIcon style={{ fontSize: "0.8rem" }} />}
									/>
								</Tooltip>
							)}
							{hasOverlap && (
								<Chip
									size="small"
									label="Has Overlap"
									style={{ backgroundColor: "#ffebee", color: "#c62828", marginRight: 8 }}
									icon={<WarningIcon style={{ fontSize: "0.8rem" }} />}
								/>
							)}
							{wasAdjusted && (
								<Chip
									size="small"
									label="Time Adjusted"
									style={{ backgroundColor: "#fff3e0", color: "#f57c00", marginRight: 8 }}
									icon={<InfoIcon style={{ fontSize: "0.8rem" }} />}
								/>
							)}
							{(coverageStatus === "COMPLETED" || coveringTrips?.length > 0) && (
								<Chip
									size="small"
									label={
										<>
											{`Covered`}
											{hasOutsideSchedule && (
												<Tooltip title={FORCED_TOOLTIP}>
													<span style={{ display: "inline-flex", marginLeft: 4, verticalAlign: "middle" }}>
														<ForcedIcon style={{ fontSize: "0.8rem" }} />
													</span>
												</Tooltip>
											)}
										</>
									}
									style={{ backgroundColor: "#e8f5e9", color: "#2e7d32", marginRight: 8 }}
									icon={<CompletedIcon style={{ fontSize: "0.8rem" }} />}
								/>
							)}
							{hasOutsideSchedule && (
								<Tooltip title="Covered by trip(s) outside schedule — forced child trip match">
									<Chip
										size="small"
										label={
											<>
												Forced child trip
												<Tooltip title={FORCED_TOOLTIP}>
													<span style={{ display: "inline-flex", marginLeft: 4, verticalAlign: "middle" }}>
														<ForcedIcon style={{ fontSize: "0.8rem" }} />
													</span>
												</Tooltip>
											</>
										}
										style={{ backgroundColor: "#fff3e0", color: "#e65100", marginRight: 8 }}
										icon={<WarningIcon style={{ fontSize: "0.8rem" }} />}
									/>
								</Tooltip>
							)}
							{coverageWarnings?.map((w) => {
								const meta = WARNING_REASON_META[w.code] || {};
								const WarnIcon = meta.icon || WarningIcon;
								let chipLabel = meta.label || w.code?.replace(/_/g, " ");
								let tooltipText = w.message || meta.label || w.code;
								if (w.code === "CHILD_TRIP_OUTSIDE_SCHEDULE" && w.metadata?.reasons?.length > 0) {
									const reasonLabels = w.metadata.reasons.map((r) => OUTSIDE_SCHEDULE_REASON_LABELS[r] || r).join(", ");
									const loadSuffix =
										w.metadata?.displaced_load_ids?.length > 0 ? ` (${w.metadata.displaced_load_ids.join(", ")})` : "";
									chipLabel = `Outside: ${reasonLabels}${loadSuffix}`;
								}
								if (w.code === "POSSIBLE_CANCELLATION") {
									const { subLabel, tooltipDetail } = formatPossibleCancellationMetadata(w);
									if (subLabel) chipLabel = `${chipLabel}: ${subLabel}`;
									if (tooltipDetail) {
										tooltipText = w.message
											? `${w.message}\n${tooltipDetail}`
											: `${meta.label || w.code} — ${tooltipDetail}`;
									}
								}
								return (
									<Tooltip key={w.code} title={tooltipText}>
										<Chip
											size="small"
											label={chipLabel}
											style={{
												backgroundColor: meta.bg || "#fff3e0",
												color: meta.color || "#ff9800",
												marginRight: 8,
												marginBottom: 4,
											}}
											icon={<WarnIcon style={{ fontSize: "0.8rem", color: meta.color || "#ff9800" }} />}
										/>
									</Tooltip>
								);
							})}
							{unavailableTrucks?.length > 0 && (
								<Tooltip
									title={`Trucks in unavailable period: ${unavailableTrucks
										.map((t) => `#${t.truckid || t._id}`)
										.join(", ")}`}
								>
									<Chip
										size="small"
										label={`Unavailable: ${unavailableTrucks.map((t) => `#${t.truckid || t._id}`).join(", ")}`}
										style={{
											backgroundColor: "#f3e5f5",
											color: "#7b1fa2",
											marginRight: 8,
											marginBottom: 4,
										}}
										icon={<EventBusyIcon style={{ fontSize: "0.8rem", color: "#7b1fa2" }} />}
									/>
								</Tooltip>
							)}
						</Box>
					)}

					{/* Timeline View */}
					{renderTimelineView(chain)}

					<Divider style={{ margin: "16px 0" }} />

					{/* Schedule Chips */}
					<Box>
						<Typography variant="subtitle2" gutterBottom>
							Schedule Details:
						</Typography>
						<Box display="flex" flexWrap="wrap">
							{schedules.map((schedule) => renderScheduleChip(schedule))}
						</Box>
					</Box>

					{/* Coordination Info */}
					{coordination && (
						<Box marginTop={2}>
							<Typography variant="caption" color="textSecondary">
								Coordination Type: {coordination.type}
							</Typography>
						</Box>
					)}
				</CardContent>
			</Card>
		);
	};

	if (loading) {
		return (
			<Box className={classes.root}>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
					<CircularProgress />
				</Box>
			</Box>
		);
	}

	if (error) {
		return (
			<Box className={classes.root}>
				<Box className={classes.emptyState}>
					<Icon color="error" style={{ fontSize: 48, marginBottom: 16 }}>
						error
					</Icon>
					<Typography variant="h6" color="error" gutterBottom>
						Error Loading Relay Chains
					</Typography>
					<Typography color="textSecondary">{error}</Typography>
				</Box>
			</Box>
		);
	}

	const chainCount = Object.keys(groupedChains).length;

	return (
		<Box className={classes.root}>
			{/* Header */}
			<Box className={classes.headerSection}>
				<Typography variant="body2" color="textSecondary">
					Showing {chainCount} instantiated relay chain{chainCount !== 1 ? "s" : ""} with coordinated pickup, longhaul,
					and delivery schedules.
				</Typography>
			</Box>

			{/* Chain Cards */}
			{chainCount === 0 ? (
				<Box className={classes.emptyState}>
					<ScheduleIcon style={{ fontSize: 48, marginBottom: 16, color: "#ccc" }} />
					<Typography variant="h6" gutterBottom>
						No Relay Chains Found
					</Typography>
					<Typography color="textSecondary">
						No instantiated relay schedule chains are available for the selected criteria.
					</Typography>
				</Box>
			) : (
				Object.values(groupedChains).map((chain) => renderChainCard(chain))
			)}
		</Box>
	);
};

export default RelayScheduleChainView;
